import { useScreenWidth } from "@msys/ui";
import { styled } from "@mui/material/styles";
import { debounce } from "lodash-es";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ExtendedNodeData,
  getFlatDataFromTree,
  getVisibleNodeCount,
  TreeItem,
} from "react-sortable-tree";
import { useLatest, useUpdateEffect } from "react-use";
import { WindowScroller } from "react-virtualized";
import { LocalDndProvider } from "../dnd.js";
import { useScroll } from "../commons/hooks/useScroll.js";
import { useScrollZone } from "../commons/hooks/useScrollZone.js";
import { TREE_SCAFFOLD_PX_WIDTH } from "../constants.js";
import { browserHasOnlyTouch } from "../featureDetection.js";
import { getDefaultCreatableSubItemTypes } from "../features/doc-items/constraints.js";
import { size } from "../../common/MuiThemeProvider.js";
import { ItemType } from "../../clients/graphqlTypes.js";
import { transientOptions } from "../styles.js";
import { InputComponent, ItemComponent } from "../trees/types.js";
import { VirtualBareTreeStandaloneItem } from "./components/VirtualBareTreeStandaloneItem.js";
import { VirtualItemNodeRenderer } from "./components/VirtualItemNodeRenderer.js";
import { StyledSortableTree } from "./components/VirtualItemSortableTree.js";
import {
  getLastSelectedNode,
  getMoveTreeItemRank,
  getNonLeafChildrenIdsRecurively,
  getOutOfScreen,
  getSyntheticRootItem,
  getTreeFromItems,
  isNodePlacedAfterInput,
  ROOT_ITEM_ID,
} from "./helpers.js";
import { ALL_KEY, ExpandedStore } from "./hooks/useExpandedStore.js";
import { useGoToItem } from "./hooks/useGoToItem.js";
import {
  AdditionalItemInput,
  VirtualItem,
  RowAnnotation,
  VirtualTreeItem,
  VirtualTreeItemContext,
} from "./types.js";

const DEFAULT_STYLE = { height: "unset" };
const RIGHT_SPACING = "12px";
const LEFT_SPACING = "12px";
const INNER_TABLET_STYLE = {
  paddingRight: RIGHT_SPACING,
  paddingLeft: LEFT_SPACING,
};
const INNER_MOBILE_STYLE = {
  paddingRight: RIGHT_SPACING,
  paddingLeft: LEFT_SPACING,
};
const ITEM_TABLET_STYLE = {
  marginLeft: `-${LEFT_SPACING}`,
};
const ITEM_MOBILE_STYLE = {
  marginLeft: `-${LEFT_SPACING}`,
};

interface Props<T extends VirtualItem> {
  top?: number;

  // Drag-reorder move handler — owned by the parent (which holds the document
  // identity). When omitted, dragging produces no move.
  moveTreeItem?: (
    itemId: string,
    parentId: string,
    rank: number
  ) => Promise<void>;
  moveTreeItemLoading?: boolean;

  items: T[];
  allItems?: T[];
  filterItem?: (item: T) => boolean;

  // virtual synthetic root item or root item id
  rootItem?: T;
  rootItemId?: string;

  rootItemComponent?: ItemComponent<T>;
  itemComponent: ItemComponent<T>;
  inputComponent?: InputComponent<T, T>;

  /**
   * Optional per-row annotation. A row that returns one is rendered taller by
   * the annotation's height, with its text outside the row's card surface.
   * Rows return null (or the prop is omitted) to keep the uniform height.
   */
  getRowAnnotation?: (node: VirtualTreeItem<T>) => RowAnnotation | null;

  selectedItemId: string | null;

  enableCreating: boolean;

  allowHaveChildren?: (item: T) => boolean;

  shouldRenderCreateInput?: (item: T) => boolean;

  documentItemTypes: ItemType[];

  container?: HTMLElement | null;

  isVirtualized?: boolean;

  onItemsCountChange?: (itemsCount: number) => void;

  expandedStore: ExpandedStore;
  // isItemExpanded: (itemId: string, defaultValue?: boolean) => boolean;
  // setItemExpanded: (itemId: string, expanded: boolean) => void;
  // setExpandedState: (state: { [key: string]: boolean }) => void;

  additionalItemInput?: AdditionalItemInput | null;

  showExpandButton?: (item: T) => boolean;
}

export const VirtualItemTree = React.memo(VirtualItemTreeComponent);

function VirtualItemTreeComponent<T extends VirtualItem>({
  moveTreeItem,
  moveTreeItemLoading = false,
  items,
  allItems,
  filterItem,
  rootItem: syntheticRootItem,
  rootItemId: syntheticRootItemId,
  selectedItemId,
  rootItemComponent,
  itemComponent,
  inputComponent,
  getRowAnnotation,
  enableCreating,
  allowHaveChildren,
  shouldRenderCreateInput,
  documentItemTypes,
  container,
  isVirtualized = true,
  onItemsCountChange,
  expandedStore,
  additionalItemInput,
  showExpandButton,
}: // isItemExpanded,
// setItemExpanded,
// setExpandedState,

Props<T>) {
  const enableDragging = moveTreeItem != null;

  const selectedItem = useMemo<T | null>(
    () => items.find(i => i.id === selectedItemId) ?? null,
    [items, selectedItemId]
  );

  const [treeWidth, setTreeWidth] = useState<number | undefined>(undefined);
  const treeWidthLatest = useLatest(treeWidth);

  const [aboveScreen, setAboveScreen] = useState<boolean>(false);
  const [belowScreen, setBelowScreen] = useState<boolean>(false);

  const aboveScreenLatest = useLatest(aboveScreen);
  const belowScreenLatest = useLatest(belowScreen);

  // @ts-ignore synthetic root item support
  const rootItem: T | undefined = useMemo(() => {
    if (syntheticRootItem) return syntheticRootItem;
    if (syntheticRootItemId)
      return getSyntheticRootItem("Item", syntheticRootItemId);
    return items.find(item => item.isRootItem);
  }, [items, syntheticRootItem, syntheticRootItemId]);

  const getTree = () => {
    return getTreeFromItems(
      items,
      rootItem,
      selectedItem,
      filterItem,
      enableCreating,
      expandedStore.isItemExpanded,
      documentItemTypes,
      shouldRenderCreateInput,
      additionalItemInput
    );
  };

  const updateTree = () => {
    const treeData = getTree();
    setQuoteItems(treeData);
    return treeData;
  };

  const [quoteItems, setQuoteItems] = useState(getTree);

  const collapseItemAndChildrenRecursively = React.useCallback(
    (node: TreeItem) => {
      const toCollapseIds = getNonLeafChildrenIdsRecurively(node);
      const toExpandItemIds = expandedStore.areAllItemsExpanded
        ? items
            .filter(
              item => item.hasChildren && !toCollapseIds.includes(item.id)
            )
            .map(item => item.id)
        : [];
      expandedStore.setExpandedState(
        Object.fromEntries([
          [ALL_KEY, false],
          ...toExpandItemIds.map(id => [id, true]),
          ...toCollapseIds.map(id => [id, false]),
        ])
      );
    },
    [expandedStore, items]
  );

  useUpdateEffect(() => {
    updateTree();
  }, [
    items,
    filterItem,
    rootItem?.id,
    selectedItem?.id,
    expandedStore.isItemExpanded,
    additionalItemInput,
  ]);

  const treeItemsCount = useMemo(
    () => getVisibleNodeCount({ treeData: quoteItems }),
    [quoteItems]
  );

  // Total extra height contributed by row annotations, so the drag-time
  // container height (getTreeHeight) is not short on an annotated tree.
  const annotationsExtraHeight = useMemo(() => {
    if (!getRowAnnotation) return 0;
    return getFlatDataFromTree({
      treeData: quoteItems,
      getNodeKey: ({ treeIndex }) => treeIndex,
      ignoreCollapsed: true,
    }).reduce(
      (sum, { node }) =>
        sum + (getRowAnnotation(node as VirtualTreeItem<T>)?.height ?? 0),
      0
    );
  }, [getRowAnnotation, quoteItems]);
  const [lastSelectedIndex, lastSelectedItemId, selectedPath] = useMemo(
    () => getLastSelectedNode(quoteItems, items, selectedItem),
    [quoteItems, items, selectedItem]
  );

  const onItemsCountChangeLatest = useLatest(onItemsCountChange);

  React.useEffect(() => {
    onItemsCountChangeLatest.current?.(treeItemsCount);
  }, [treeItemsCount, onItemsCountChangeLatest]);

  const windowScrollerRef = useRef<any>(null);

  const generateNodeProps = useCallback(
    ({ node }: ExtendedNodeData): VirtualTreeItemContext<T> => {
      return {
        selectedItemId: selectedItem?.id ?? null,
        lastSelectedItemId,
        rootItem,
        items,
        allItems,
        rootItemComponent,
        itemComponent,
        inputComponent,
        getRowAnnotation,
        showExpandButton: showExpandButton ?? (item => item.hasChildren),
      };
    },
    [
      selectedItem?.id,
      lastSelectedItemId,
      rootItem,
      items,
      allItems,
      rootItemComponent,
      itemComponent,
      inputComponent,
      getRowAnnotation,
      showExpandButton,
    ]
  );

  const { isMaxPhone } = useScreenWidth();

  const goToItem = useGoToItem(
    windowScrollerRef,
    updateTree,
    expandedStore.setExpandedState,
    container
  );

  const onScroll = useCallback(
    ({ y }: { y: number }) => {
      if (lastSelectedIndex === null) {
        if (aboveScreenLatest.current) setAboveScreen(false);
        if (belowScreenLatest.current) setBelowScreen(false);
        return;
      }
      const top: number = windowScrollerRef.current?._positionFromTop ?? 0;
      const scrollTop: number = y - top;
      const windowHeight = container
        ? container.offsetHeight
        : window.innerHeight;
      const [above, below] = getOutOfScreen(
        lastSelectedIndex,
        size.treeItemFullHeight,
        scrollTop,
        windowHeight
      );
      if (above !== aboveScreenLatest.current) setAboveScreen(above);
      if (below !== belowScreenLatest.current) setBelowScreen(below);
    },
    [lastSelectedIndex, aboveScreenLatest, belowScreenLatest, container]
  );

  useScroll(container ?? window, onScroll);

  useUpdateEffect(() => {
    const y = container ? container.scrollTop : window.scrollY;
    onScroll({ y });
  }, [container, lastSelectedIndex]);

  const dragging = useRef<boolean>(false);
  const draggingHeight = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const virtualizedListRef = useRef<any>(null);

  // react-virtualized caches row sizes, and react-sortable-tree never
  // invalidates them. Without this, changing which row carries an annotation
  // (selection moves the create-input row) leaves the cached taller size on the
  // old index and an unrelated row renders too tall.
  //
  // virtualizedListRef points at react-sortable-tree's drag-scroll HOC, not at
  // the react-virtualized List — the List is on the HOC's wrappedInstance ref
  // (the same handle patches/frontend-collective-react-dnd-scrollzone reaches
  // through). Every hop is optional and the call is type-checked: this is a
  // rendering-time nicety and must never be able to throw.
  React.useEffect(() => {
    if (!getRowAnnotation) return;
    const list = virtualizedListRef.current?.wrappedInstance?.current;
    if (typeof list?.recomputeRowHeights === "function") {
      list.recomputeRowHeights();
    }
  }, [getRowAnnotation, quoteItems, selectedItem?.id]);

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const handleResize = debounce(() => {
      if (!scrollContainerRef.current) return;
      const { width } = scrollContainerRef.current.getBoundingClientRect();
      if (treeWidthLatest.current !== width) setTreeWidth(width);
    });

    if (typeof ResizeObserver !== "undefined" && scrollContainerRef.current) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [treeWidthLatest]);

  const { startDrag, endDrag } = useScrollZone(container);

  return (
    <LocalDndProvider>
      {/*@ts-ignore render return type is incompatible*/}
      <WindowScroller
        ref={windowScrollerRef}
        scrollElement={isVirtualized ? (container ?? undefined) : undefined}
      >
        {({ height, isScrolling, onChildScroll, scrollTop }) => {
          return (
            <>
              {aboveScreen && selectedItem && (
                <PageFixedColumn $top={0}>
                  <PageFixedColumnInner $top={0}>
                    <VirtualBareTreeStandaloneItem
                      item={selectedItem!}
                      items={items}
                      depth={selectedPath.length}
                      itemComponent={
                        selectedItem!.isRootItem
                          ? (rootItemComponent ?? itemComponent)
                          : itemComponent
                      }
                      selected
                      clickable
                      onClick={() => goToItem(selectedItem, items)}
                    />
                  </PageFixedColumnInner>
                </PageFixedColumn>
              )}
              <ScrollContainer
                ref={scrollContainerRef}
                $itemsCount={treeItemsCount}
                $extraHeight={annotationsExtraHeight}
                $isVirtualized={isVirtualized}
              >
                <StyledSortableTree
                  isVirtualized={isVirtualized}
                  style={DEFAULT_STYLE}
                  innerStyle={
                    isMaxPhone ? INNER_MOBILE_STYLE : INNER_TABLET_STYLE
                  }
                  generateNodeProps={generateNodeProps}
                  reactVirtualizedListProps={{
                    autoHeight: true,
                    height,
                    width: treeWidth,
                    isScrolling,
                    scrollTop,
                    onScroll: onChildScroll,
                    overscanRowCount: 4,
                    ref: virtualizedListRef,
                  }}
                  scaffoldBlockPxWidth={TREE_SCAFFOLD_PX_WIDTH}
                  rowHeight={
                    getRowAnnotation
                      ? ({ node }: { node: TreeItem }) =>
                          size.treeItemFullHeight +
                          (getRowAnnotation(node as VirtualTreeItem<T>)
                            ?.height ?? 0)
                      : size.treeItemFullHeight
                  }
                  canDrag={({ node }) =>
                    !moveTreeItemLoading && enableDragging && !node.isInput
                  }
                  canDrop={({ node, nextParent, nextPath, prevPath }) => {
                    if (moveTreeItemLoading) return false;
                    if (dragging.current && scrollContainerRef.current?.style) {
                      // calculate new height during dragging
                      const rowCount =
                        virtualizedListRef.current?.props?.rowCount;
                      const newHeight = getTreeHeight(
                        rowCount,
                        annotationsExtraHeight
                      );
                      if (draggingHeight.current !== newHeight) {
                        draggingHeight.current = newHeight;
                        scrollContainerRef.current.style.height = `${newHeight}px`;
                      }
                    }

                    const nextParentItem = (nextParent?.item || rootItem) as T;

                    if (
                      "linkedQuoteTemplate" in nextParentItem &&
                      Boolean(nextParentItem.linkedQuoteTemplate)
                    ) {
                      return false;
                    }

                    // an item dropped into an eliminated subtree would vanish
                    // from every view right away
                    if (
                      nextParentItem.isItemEliminated ||
                      ("isAnyParentItemEliminated" in nextParentItem &&
                        nextParentItem.isAnyParentItemEliminated === true)
                    ) {
                      return false;
                    }

                    if (
                      !getDefaultCreatableSubItemTypes(
                        nextParentItem.type,
                        documentItemTypes
                      ).includes(node.type)
                    ) {
                      return false;
                    }
                    if (nextParent?.isInput) {
                      return false;
                    }
                    if (
                      syntheticRootItemId === ROOT_ITEM_ID &&
                      (nextParent?.id === syntheticRootItemId ||
                        nextParent?.id === syntheticRootItem?.id)
                    ) {
                      return false;
                    }
                    if (
                      isNodePlacedAfterInput(
                        quoteItems,
                        node,
                        nextPath,
                        prevPath
                      )
                    ) {
                      return false;
                    }
                    return true;
                  }}
                  canNodeHaveChildren={node =>
                    !node.isInput &&
                    allowHaveChildren?.(node.item as T) !== false
                  }
                  treeData={quoteItems}
                  onChange={setQuoteItems}
                  onMoveNode={async ({ node, treeData, nextParentNode }) => {
                    const parentId = (nextParentNode?.id ??
                      rootItem?.id) as string;
                    const visibleSiblingIds = (
                      (nextParentNode?.children as TreeItem[]) ?? treeData
                    )
                      .filter(node => !node.isInput)
                      .map(node => node.id as string);
                    const rank = getMoveTreeItemRank({
                      allItems: allItems ?? items,
                      newParentId: parentId,
                      movedItemId: node.id,
                      visibleSiblingIds,
                    });
                    await moveTreeItem?.(node.id, parentId, rank);
                  }}
                  onVisibilityToggle={({ node, expanded }) => {
                    if (expanded === true) {
                      expandedStore.setItemExpanded(node.id, expanded);
                    } else {
                      collapseItemAndChildrenRecursively(node);
                    }
                  }}
                  onDragStateChanged={({ isDragging }) => {
                    if (browserHasOnlyTouch) {
                      if (isDragging) {
                        startDrag();
                      } else {
                        endDrag();
                      }
                    }
                    dragging.current = isDragging;
                    draggingHeight.current = isDragging
                      ? getTreeHeight(treeItemsCount, annotationsExtraHeight)
                      : 0;
                    if (!isDragging && scrollContainerRef.current?.style)
                      scrollContainerRef.current.style.height = "";
                  }}
                  // @ts-ignore
                  nodeContentRenderer={VirtualItemNodeRenderer}
                />
              </ScrollContainer>
              {belowScreen && selectedItem && (
                <PageFixedColumn $bottom={0}>
                  <PageFixedColumnInner $bottom={0}>
                    <VirtualBareTreeStandaloneItem
                      item={selectedItem}
                      items={items}
                      depth={selectedPath.length}
                      itemComponent={
                        selectedItem.isRootItem
                          ? (rootItemComponent ?? itemComponent)
                          : itemComponent
                      }
                      selected
                      clickable
                      onClick={() => goToItem(selectedItem, items)}
                    />
                  </PageFixedColumnInner>
                </PageFixedColumn>
              )}
            </>
          );
        }}
      </WindowScroller>
    </LocalDndProvider>
  );
}

const PageFixedColumn = styled("div", transientOptions)<{
  $top?: number;
  $bottom?: number;
}>`
  z-index: 20;
  position: sticky;

  width: 100%;
  height: 0;

  ${({ $top }) =>
    $top !== undefined
      ? `
        top: ${$top}px;
      `
      : ""}

  ${({ $bottom }) =>
    $bottom !== undefined
      ? `
        bottom: ${$bottom}px;
      `
      : ""}
`;

const PageFixedColumnInner = styled("div", transientOptions)<{
  $top?: number;
  $bottom?: number;
}>`
  z-index: 21;
  position: absolute;
  width: 100%;

  ${({ $top }) =>
    $top !== undefined
      ? `
        top: 0;
        padding-top: env(safe-area-inset-top);
      `
      : ""}

  ${({ $bottom }) =>
    $bottom !== undefined
      ? `
        bottom: 0;
        padding-bottom: env(safe-area-inset-bottom);
      `
      : ""}
`;

const ScrollContainer = styled(
  "div",
  transientOptions
)<{
  $itemsCount: number;
  $extraHeight: number;
  $isVirtualized: boolean;
}>(
  ({ $isVirtualized, $itemsCount, $extraHeight }) => `
  ${
    $isVirtualized
      ? `
        margin-right: -${RIGHT_SPACING};
        margin-left: -${LEFT_SPACING};
      `
      : ""
  }
  box-sizing: content-box;
  position: relative;
  height: ${getTreeHeight($itemsCount, $extraHeight)}px;
`
);

// extraHeight covers row annotations, which make individual rows taller than
// the uniform size.treeItemFullHeight. Every caller must pass it, or the
// container ends up shorter than its rows and content overflows the tree.
const getTreeHeight = (itemsCount: number, extraHeight: number) =>
  size.treeItemFullHeight * itemsCount + extraHeight;
